import React from 'react';
import { useMsal } from '@azure/msal-react';
import { useCurrentUser } from '@/components/auth/AuthProvider';
import { useRole } from '@/hooks/useRole';
import { LogOut, User, Mail, Shield, Bell, Moon, Loader2 } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { authFetch } from '@/utils/authFetch';
import {
  requestNotificationPermission,
  subscribeToPush,
  registerServiceWorker,
} from '@/services/notificationService';
import toast from 'react-hot-toast';

export function ProfilePage() {
  const { instance } = useMsal();
  const user = useCurrentUser();
  const { theme, toggleTheme } = useTheme();
  const { isAdmin, isSupervisor, loading: roleLoading } = useRole();

  const [notificationsEnabled, setNotificationsEnabled] = React.useState(false);
  const [loadingNotifPref, setLoadingNotifPref] = React.useState(true);

  // Derive display role from permissions system — single source of truth
  const displayRole = roleLoading
    ? '...'
    : isAdmin
    ? 'admin'
    : isSupervisor
    ? 'supervisor'
    : 'inspector';

  // Load saved notification preference on mount
  React.useEffect(() => {
    if (!user) return;
    authFetch(`/api/preferences/${user.id}`)
      .then(r => r.json())
      .then(prefs => setNotificationsEnabled(prefs.pushNotificationsEnabled ?? false))
      .catch(console.error)
      .finally(() => setLoadingNotifPref(false));
  }, [user?.id]);

  async function handleNotificationToggle() {
    if (!user) return;
    const newValue = !notificationsEnabled;

    if (newValue) {
      await registerServiceWorker();
      const granted = await requestNotificationPermission();
      if (!granted) {
        toast.error('Notification permission denied. Please enable in browser settings.');
        return;
      }
      await subscribeToPush(user.id, user.email);
    }

    try {
      await authFetch(`/api/preferences/${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pushNotificationsEnabled: newValue }),
      });
      setNotificationsEnabled(newValue);
      toast.success(newValue ? 'Push notifications enabled' : 'Push notifications disabled');
    } catch {
      toast.error('Failed to save preference. Please try again.');
    }
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6 dark:bg-gray-800 min-h-full">
      <h1 className="text-2xl font-bold text-wire3-navy dark:text-wire3-green">Profile</h1>

      {user && (
        <div className="mt-5 card dark:bg-gray-750 dark:border-gray-700 flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-wire3-green/10 text-wire3-green">
            <User className="h-7 w-7" />
          </div>
          <div>
            <p className="font-bold text-wire3-navy dark:text-gray-100">{user.name}</p>
            <p className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
              <Mail className="h-3.5 w-3.5" />
              {user.email}
            </p>
            <p className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 capitalize">
              <Shield className="h-3.5 w-3.5" />
              {displayRole}
            </p>
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          Settings
        </h3>
        <div className="mt-2 flex flex-col gap-1">
          <SettingRow
            icon={<Bell className="h-5 w-5" />}
            label="Push Notifications"
            toggle
            on={notificationsEnabled}
            onToggle={handleNotificationToggle}
            loading={loadingNotifPref}
          />
          <SettingRow
            icon={<Moon className="h-5 w-5" />}
            label="Dark Mode"
            toggle
            on={theme === 'dark'}
            onToggle={toggleTheme}
          />
        </div>
      </div>

      {/* Sign out */}
      <button
        onClick={() => instance.logoutRedirect()}
        className="btn-danger mt-8 w-full"
      >
        <LogOut className="h-4 w-4" />
        Sign Out
      </button>

      <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
        Wire3 Vehicle Inspection v1.0.0
      </p>
    </div>
  );
}

function SettingRow({
  icon, label, toggle, on, onToggle, loading,
}: {
  icon: React.ReactNode;
  label: string;
  toggle?: boolean;
  on?: boolean;
  onToggle?: () => void;
  loading?: boolean;
}) {
  const [localOn, setLocalOn] = React.useState(false);
  const isOn = on !== undefined ? on : localOn;
  const handleToggle = onToggle ?? (() => setLocalOn(v => !v));

  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-700">
      <div className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
        <span className="text-gray-400 dark:text-gray-500">{icon}</span>
        <span className="text-sm font-medium">{label}</span>
      </div>
      {toggle && (
        loading ? (
          <div className="flex h-6 w-11 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-600">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
          </div>
        ) : (
          <button
            onClick={handleToggle}
            className={`relative h-6 w-11 rounded-full transition-colors ${
              isOn ? 'bg-wire3-green' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                isOn ? 'left-[22px]' : 'left-0.5'
              }`}
            />
          </button>
        )
      )}
    </div>
  );
}
